// NewListingCard.js
import React from 'react';
import { NamedLink, ResponsiveImage, AspectRatioWrapper } from '../../../components';
import { lazyLoadWithDimensions } from '../../../util/uiHelpers';
import css from './NewListingCard.module.css';
import { ensureListing } from '../../../util/data';
import { createSlug } from '../../../util/urlHelpers';
import { isFieldForCategory, isFieldForListingType } from '../../../util/fieldHelpers';
import { useConfiguration } from '../../../context/configurationContext';
import { useIntl } from 'react-intl';
import SectionGallery from '../../ListingPage/SectionGallery';

const LazyImage = lazyLoadWithDimensions(ResponsiveImage, { loadAfterInitialRendering: 3000 });

const NewListingCard = ({ listing }) => {
  const currentListing = ensureListing(listing);
  const id = currentListing.id.uuid;
  const { title = '', publicData } = currentListing.attributes;
  const config = useConfiguration();
  const intl = useIntl();

  const slug = createSlug(title);
  const {
    aspectWidth = 1,
    aspectHeight = 1,
    variantPrefix = 'listing-card',
  } = config.layout.listingImage;

  const priceObj = currentListing.attributes?.price;
  const price = priceObj
    ? new Intl.NumberFormat('en-US', {
        style: 'currency',
        currency: priceObj.currency,
      }).format(priceObj.amount / 100)
    : 'N/A';
  const firstImage = currentListing.images?.[0] || null;

  const variants = firstImage
    ? Object.keys(firstImage?.attributes?.variants).filter(k => k.startsWith(variantPrefix))
    : [];

  //helper function for extracting details
  const metadata = {};

  const pickListingFields = (filteredConfigs, config) => {
    const { key, schemaType, enumOptions, showConfig = {} } = config;
    const listingType = publicData.listingType;
    const isTargetListingType = isFieldForListingType(listingType, config);
    const isTargetCategory = isFieldForCategory(config);

    const { isDetail, label } = showConfig;
    const publicDataValue = publicData[key];
    const metadataValue = metadata[key];
    const value = typeof publicDataValue != null ? publicDataValue : metadataValue;

    if (isDetail && isTargetListingType && isTargetCategory && typeof value !== 'undefined') {
      const findSelectedOption = enumValue => enumOptions?.find(o => enumValue === `${o.option}`);
      const getBooleanMessage = value =>
        value
          ? intl.formatMessage({ id: 'SearchPage.detailYes' })
          : intl.formatMessage({ id: 'SearchPage.detailNo' });
      const optionConfig = findSelectedOption(value);

      return schemaType === 'enum'
        ? filteredConfigs.concat({ key, value: optionConfig?.label, label })
        : schemaType === 'boolean'
        ? filteredConfigs.concat({ key, value: getBooleanMessage(value), label })
        : schemaType === 'long'
        ? filteredConfigs.concat({ key, value, label })
        : filteredConfigs;
    }
    return filteredConfigs;
  };

  const listingFieldConfigs = config.listing.listingFields;
  const existingListingFields = listingFieldConfigs.reduce(pickListingFields, []);

  //helper function to get value by label
  function getValueByLabel(existingListingFields, targetLabel) {
    const item = existingListingFields.find(entry => entry.label.includes(targetLabel));
    return item ? item.value : null;
  }
  
  //The label value 'Size' is from console
  const sizeValue = getValueByLabel(existingListingFields, 'Size');
  const size = sizeValue ? sizeValue : 'N/A';

  return (
    <NamedLink name="ListingPage" params={{ id, slug }} className={css.cardLink} key={id}>
      <div className={css.card}>
        <AspectRatioWrapper width={3} height={2} className={css.imageWrapper}>
          <SectionGallery
              listing={currentListing}
              variantPrefix={config.layout.listingImage.variantPrefix}
              isLandingPage={true}
            />
        </AspectRatioWrapper>

        <div className={css.details}>
          <div className={css.title}>{title}</div>
          <div className={css.meta}>
            <span className={css.price}>{price}</span>
            <span className={css.size}>Size: {size}</span>
          </div>
        </div>
      </div>
    </NamedLink>
  );
};

export default NewListingCard;
